import { useEffect, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import { createContext } from 'use-context-selector';
import { getCustomRedirectIdentityLoginUrl } from "libs/shared-defaults/src/lib/identity";
import { getAccessTokenFromStorage, setAccessTokenInStorage, removeAccessTokenInStorage } from "../../defaults/auth-control";
import { isTokenExpired } from "../../defaults/auth-control/token";
import { getHashParams } from "@frontend/shared-utility";

export const AuthContext = createContext({});

// Drop the SSO implicit-grant fragment (#access_token=...&expires_in=...&token_type=...)
// from the URL without a reload or new history entry, keeping path and query. The hash is
// parsed as key/value pairs and only cleared when it actually holds an access_token key, so
// the whole OAuth fragment is removed atomically while any other anchor is left untouched.
// Best-effort: never block auth.
const stripAccessTokenFromUrl = () => {
  if (!window.history?.replaceState) {
    return;
  }

  try {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));

    if (hashParams.has('access_token')) {
      url.hash = '';
    }

    window.history.replaceState(null, document.title, `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // no-op
  }
};

const AuthProvider = ({ children, eventTrack, getLoginUrl = getCustomRedirectIdentityLoginUrl, keySuffix = '' }) => {

  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    const tokenFromParams = getHashParams(window.location.href).access_token;
    const tokenFromStorage = getAccessTokenFromStorage({ keySuffix });
    const token = tokenFromParams || tokenFromStorage;

    // An EXPIRED stored token must be treated as "no session": otherwise it is
    // accepted here and the expiry is only discovered later, by the first API
    // call that gets a 401 — at which point the logout path may pick the wrong
    // login (portal instead of sandbox). Redirect through `getLoginUrl`, which
    // the caller already made sandbox-aware, so the user lands on the correct
    // login. A token straight from the SSO fragment is fresh, so it is never
    // expired away. Clear the stale token first so it does not leak into the
    // next mount / a non-sandbox path.
    if (!token || (!tokenFromParams && isTokenExpired(token))) {
      if (token) {
        removeAccessTokenInStorage({ keySuffix });
      }
      window.location.href = getLoginUrl(window.location.href);
      return;
    }

    if (tokenFromParams && eventTrack) {
      eventTrack();
    }

    setAccessToken(token);
    setAccessTokenInStorage(token, { keySuffix });

    if (tokenFromParams) {
      stripAccessTokenFromUrl();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    accessToken,
    setAccessToken,
  }), [accessToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.element,
  eventTrack: PropTypes.func,
  getLoginUrl: PropTypes.func,
  keySuffix: PropTypes.string
};

export default AuthProvider;
